import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { effectivePrice } from "@/lib/format";

const CartContext = createContext(null);
const KEY = "eclat_cart";

function lineId(productId, size, color) {
  return `${productId}::${size || ""}::${color || ""}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, { quantity = 1, size = null, color = null } = {}) => {
    const id = lineId(product.id, size, color);
    setItems((prev) => {
      const existing = prev.find((i) => i.line_id === id);
      if (existing) {
        return prev.map((i) =>
          i.line_id === id ? { ...i, quantity: Math.min(i.quantity + quantity, product.stock || 99) } : i
        );
      }
      return [
        ...prev,
        {
          line_id: id,
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          image: product.images?.[0] || "",
          price: effectivePrice(product),
          quantity,
          size,
          color,
          stock: product.stock,
        },
      ];
    });
  };

  const updateQuantity = (lineIdVal, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (i.line_id === lineIdVal ? { ...i, quantity: Math.max(1, quantity) } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const removeItem = (lineIdVal) => setItems((prev) => prev.filter((i) => i.line_id !== lineIdVal));
  const clearCart = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQuantity, removeItem, clearCart, subtotal, count }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
